import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedClient } from "./queries";
import type { components } from "./schema";

export type DeliveryFee = components["schemas"]["ManufacturerDeliveryFeeRead"];
export type DeliveryAccessorial = DeliveryFee["accessorial"];

const deliveryFeesKey = (manufacturerUuid: string) =>
  ["manufacturers", manufacturerUuid, "delivery-fees"] as const;

export function useManufacturerDeliveryFees(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: deliveryFeesKey(manufacturerUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/manufacturers/{uuid}/delivery-fees",
        { params: { path: { uuid: manufacturerUuid } } },
      );
      if (error) throw error;
      return data;
    },
  });
}

/** Set a manufacturer's fee for one delivery extra; `amount: null` reverts to the default. */
export function useUpdateManufacturerDeliveryFee(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const key = deliveryFeesKey(manufacturerUuid);
  return useMutation({
    mutationFn: async ({
      accessorial,
      amount,
    }: {
      accessorial: DeliveryAccessorial;
      amount: string | null;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/manufacturers/{uuid}/delivery-fees/{accessorial}",
        {
          params: { path: { uuid: manufacturerUuid, accessorial } },
          body: { amount },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ accessorial, amount }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<DeliveryFee[]>(key);
      queryClient.setQueryData<DeliveryFee[]>(key, (fees) =>
        fees?.map((fee) =>
          fee.accessorial === accessorial
            ? { ...fee, amount, notes: amount === null ? null : fee.notes }
            : fee,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (fees) => {
      queryClient.setQueryData(key, fees);
    },
  });
}
